/**
 * Paquetes de marca.
 *
 * Esta instalacion se vende con una sola identidad: Sweet Calendar. La estructura de paquete
 * se conserva para que una marca nueva entre con datos y no con codigo: NEXT_PUBLIC_SWEET_MARCA
 * elige el paquete y cada despliegue es su propio build.
 *
 * Aqui solo viven DATOS (sin JSX y sin importar nada del repo) para que lo pueda leer tanto el
 * servidor como el navegador, y para que packages/lib no dependa de nadie.
 *
 * Los simbolos y wordmarks NO se redibujan: son los archivos del disenador, servidos desde
 * apps/web/public/marcas/<id>/. Ver packages/ui/components/logo/marks.tsx.
 */

export interface ColoresDeMarca {
  /** Acento vivo sobre fondo claro. Pinta simbolos, focos, interruptores y horarios elegidos. */
  acento: string;
  /** Acento sobre fondo oscuro (mas claro, para que se lea). */
  acentoOscuro: string;
  /** Version profunda para botones con texto blanco y texto de acento sobre claro (contraste AA). */
  acentoFuerte: string;
  /** Color de apoyo de la marca. Se usa con cuentagotas. */
  apoyo: string;
  /** Lavado del acento: fondos suaves, dias seleccionables, el chip de "hoy". */
  acentoLavado: string;
  /** Texto principal y superficies invertidas. */
  tinta: string;
  /** Fondo de pagina. */
  fondo: string;
  /** Un paso mas apagado que el fondo, para separar zonas. */
  fondoApagado: string;
  /** Lineas y bordes. */
  borde: string;
  /** Color de la barra del navegador en movil. */
  navegador: string;
}

export interface PaqueteDeMarca {
  id: string;
  /** Nombre por defecto del producto. NEXT_PUBLIC_APP_NAME lo puede pisar. */
  nombre: string;
  /** Quien lo hace. Aparece en el pie y en la firma de los correos. */
  empresa: string;
  correoSoporte: string;
  sitio: string;
  terminos: string;
  privacidad: string;
  colores: ColoresDeMarca;
  /** Familia tipografica del wordmark y los titulos de portada. */
  fuenteDisplay: "bungee";
  /** Como se pinta el wordmark: el simbolo con la palabra al lado, o un archivo que ya la trae dentro. */
  wordmark: "simbolo-mas-texto" | "archivo";
}

export const MARCAS: Record<string, PaqueteDeMarca> = {
  // Sweet Calendar: el producto que se vende y el que usa Sweet Vibes en casa.
  // Valores tomados de vibe-studio/packages/ui/src/tokens.css (fuente unica de la suite).
  sweet: {
    id: "sweet",
    nombre: "Sweet Calendar",
    empresa: "Sweet Vibes",
    correoSoporte: "hola@sweetvibes.ai",
    sitio: "https://sweetvibes.ai",
    terminos: "/terminos",
    privacidad: "/privacidad",
    colores: {
      acento: "#e91e63",
      acentoOscuro: "#ff5c8d",
      acentoFuerte: "#c2185b",
      apoyo: "#ffd60a",
      acentoLavado: "#fde4ec",
      tinta: "#0f0828",
      fondo: "#f5f2eb",
      fondoApagado: "#efeae0",
      borde: "#e3ddd1",
      navegador: "#f5f2eb",
    },
    fuenteDisplay: "bungee",
    wordmark: "simbolo-mas-texto",
  },
};

export const MARCA_POR_DEFECTO = "sweet";

/** Id de la marca activa. Se inlinea en build: cada marca es su propio despliegue. */
export const ID_DE_MARCA =
  process.env.NEXT_PUBLIC_SWEET_MARCA && MARCAS[process.env.NEXT_PUBLIC_SWEET_MARCA]
    ? process.env.NEXT_PUBLIC_SWEET_MARCA
    : MARCA_POR_DEFECTO;

export const MARCA: PaqueteDeMarca = MARCAS[ID_DE_MARCA];

/** Ruta publica de un archivo de marca. Todas las marcas viajan en el build; cambia la carpeta. */
export const archivoDeMarca = (nombre: string) => `/marcas/${ID_DE_MARCA}/${nombre}`;
